import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronDown, Instagram, MessageCircle, TrendingUp, Heart, MousePointer, Users } from "lucide-react";

const channels = [
  {
    id: 'social',
    title: 'Organic Social Media',
    subtitle: 'Updated for 2026',
    icon: Instagram,
    gradient: 'from-pink-500 to-purple-600',
    color: '#a855f7',
    oneLiner: 'Build community & authentic connections',
    isRich: true,
    modal: {
      emoji: '📱',
      headline: 'Organic Social Media',
      tagline: 'Updated for 2026 Information',
    }
  },
  {
    id: 'email',
    title: 'Email Marketing',
    subtitle: 'Newsletters & Campaigns',
    icon: MessageCircle,
    gradient: 'from-blue-500 to-cyan-600',
    color: '#06b6d4',
    oneLiner: 'Highest ROI channel: $36 for every $1 spent',
    modal: {
      emoji: '✉️',
      headline: 'Email Marketing',
      tagline: 'The most profitable channel in marketing. Period.',
      intro: 'Email marketing lets you speak directly to people who already know you. No algorithm. No pay-per-click. Just your message, in their inbox, when they\'re ready to read it.',
      sections: [
        {
          title: 'Why It Matters',
          color: '#06b6d4',
          items: [
            '$36 return for every $1 spent: highest ROI of any channel',
            'You OWN your email list (unlike social followers)',
            'Direct line to your most interested customers',
            'Automated sequences nurture leads while you sleep'
          ]
        },
        {
          title: 'Types of Emails That Convert',
          color: '#10b981',
          items: [
            'Welcome series: first impression matters',
            'Promotional offers: timely deals & seasonal campaigns',
            'Educational content: become the expert they trust',
            'Re-engagement: win back inactive subscribers'
          ]
        }
      ],
      stat: { value: '$36', label: 'Average return for every $1 spent on email marketing' },
      tip: 'The best email strategy isn\'t about selling every email. It\'s about providing value 80% of the time and asking for the sale 20%.'
    }
  },
  {
    id: 'seo',
    title: 'Website & SEO',
    subtitle: 'Search Engine Optimization',
    icon: TrendingUp,
    gradient: 'from-green-500 to-emerald-600',
    color: '#10b981',
    oneLiner: 'Free traffic from people actively searching for you',
    modal: {
      emoji: '🔍',
      headline: 'Website & SEO',
      tagline: 'Be found when people are already looking for what you offer.',
      intro: 'SEO (Search Engine Optimization) is the process of making your website show up in Google when someone searches for your product or service. Unlike paid ads, these clicks are free. And they compound over time.',
      sections: [
        {
          title: 'Why It Matters',
          color: '#10b981',
          items: [
            '68% of all online experiences start with a search engine',
            'Organic results get 70% of all clicks (vs. 30% for ads)',
            'Results compound: a good page keeps bringing traffic for years',
            'Builds credibility: ranking high signals trust to consumers'
          ]
        },
        {
          title: 'SEO Essentials',
          color: '#06b6d4',
          items: [
            'Claim & optimize your Google Business Profile',
            'Make sure your site loads fast and works on mobile',
            'Create content that answers questions your customers ask',
            'Get listed in local directories and earn reviews'
          ]
        }
      ],
      stat: { value: '68%', label: 'of all online experiences begin with a search engine' },
      tip: 'SEO is a long game: it takes 3-6 months to see results. But once you rank, you get free, qualified traffic every single day.'
    }
  },
  {
    id: 'reviews',
    title: 'Reviews & Reputation',
    subtitle: 'Google, Yelp & Social Proof',
    icon: Heart,
    gradient: 'from-amber-500 to-yellow-600',
    color: '#f59e0b',
    oneLiner: '93% of consumers read reviews before purchasing',
    modal: {
      emoji: '⭐',
      headline: 'Reviews & Reputation',
      tagline: 'What others say about you matters more than what you say about yourself.',
      intro: 'Online reviews are the modern word-of-mouth. Before calling, visiting, or buying, most people check your reviews on Google, Yelp, or Facebook. A strong review profile is one of the most powerful trust signals you can have.',
      sections: [
        {
          title: 'Why It Matters',
          color: '#f59e0b',
          items: [
            '93% of consumers say reviews influence their purchase decision',
            'A 1-star increase on Yelp leads to a 5-9% increase in revenue',
            'Google uses reviews as a local ranking factor',
            'Negative reviews, handled well, actually build more trust'
          ]
        },
        {
          title: 'Best Practices',
          color: '#10b981',
          items: [
            'Ask happy customers for reviews (in person, by email, via text)',
            'Respond to EVERY review: positive and negative',
            'Never buy fake reviews: platforms detect them and penalize you',
            'Feature your best reviews on your website and social media'
          ]
        }
      ],
      stat: { value: '93%', label: 'of consumers read online reviews before making a purchase' },
      tip: 'The best time to ask for a review is right after a positive experience. Make it easy: send them a direct link to your Google review page.'
    }
  },
  {
    id: 'local',
    title: 'Local Presence',
    subtitle: 'Maps, Directories & Listings',
    icon: MousePointer,
    gradient: 'from-red-500 to-orange-600',
    color: '#ef4444',
    oneLiner: '"Near me" searches have grown 500%+',
    modal: {
      emoji: '📍',
      headline: 'Local Presence',
      tagline: 'When they search "near me," will they find you?',
      intro: 'Local presence is about making sure your business shows up correctly everywhere people look: Google Maps, Apple Maps, Yelp, Facebook, industry directories, and more. Incorrect or missing information costs you customers every day.',
      sections: [
        {
          title: 'Why It Matters',
          color: '#ef4444',
          items: [
            '"Near me" searches have grown over 500% in recent years',
            '76% of people who search for something nearby visit within 24 hours',
            'Inconsistent business info (hours, phone, address) erodes trust',
            'Local listings directly impact your Google search ranking'
          ]
        },
        {
          title: 'Key Actions',
          color: '#f59e0b',
          items: [
            'Claim your Google Business Profile and keep it updated',
            'Ensure NAP (Name, Address, Phone) is identical everywhere',
            'Add photos: listings with photos get 42% more direction requests',
            'List in industry-specific directories (not just Google)'
          ]
        }
      ],
      stat: { value: '76%', label: 'of people who search nearby visit a business within 24 hours' },
      tip: 'Do a quick Google search for your own business. Is everything accurate? Hours, phone number, website, address? If not, that\'s your #1 priority.'
    }
  },
  {
    id: 'referral',
    title: 'Referral & Word of Mouth',
    subtitle: 'Your Most Trusted Channel',
    icon: Users,
    gradient: 'from-violet-500 to-indigo-600',
    color: '#8b5cf6',
    oneLiner: '92% trust recommendations from people they know',
    modal: {
      emoji: '🤝',
      headline: 'Referral & Word of Mouth',
      tagline: 'The most trusted form of marketing: one person telling another.',
      intro: 'Word-of-mouth marketing happens when your customers become your advocates. It\'s the most trusted form of advertising because it comes from real people, not brands. And with the right systems, you can encourage it to happen more often.',
      sections: [
        {
          title: 'Why It Matters',
          color: '#8b5cf6',
          items: [
            '92% of consumers trust referrals from people they know',
            'Referred customers have a 37% higher retention rate',
            'Word-of-mouth drives $6 trillion in annual consumer spending',
            'A referred customer\'s lifetime value is 16% higher'
          ]
        },
        {
          title: 'How to Encourage It',
          color: '#10b981',
          items: [
            'Deliver an experience worth talking about',
            'Create a simple referral program with real incentives',
            'Ask for referrals at the right moment (after a win)',
            'Make it easy to share: provide referral links or cards'
          ]
        }
      ],
      stat: { value: '92%', label: 'of consumers trust recommendations from people they know' },
      tip: 'The best referral programs are dead simple: "Send a friend, you both get $X." Remove every barrier to sharing.'
    }
  }
];

const OrganicSocialModal = ({ channel, onClose }) => {
  if (!channel) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed top-0 right-0 bottom-0 z-[101] w-[50%] max-w-[960px] bg-slate-900 shadow-2xl flex flex-col border-l border-slate-700"
      >
        {/* Header - prominent gradient bar */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600">
          <div className="flex items-center gap-3 min-w-0 mr-4">
            <button onClick={onClose} className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0 font-medium" data-testid="close-organic-modal">
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back to Report</span>
            </button>
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
              <Instagram className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">Organic Social Media</p>
              <p className="text-white/60 text-xs">Updated for 2026 Information</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15 transition-colors">
            <X className="w-4 h-4 text-white/80" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">

        {/* Intro */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-5 border border-slate-700">
          <p className="text-slate-300 text-sm leading-relaxed">
            Organic social media is the <span className="text-purple-400 font-semibold">unpaid content</span> you post on platforms like Facebook, Instagram, and TikTok. It's how your brand stays visible, relatable, and top-of-mind. But in 2026, the landscape has changed dramatically: here's what every business needs to know.
          </p>
        </div>

        {/* The Reality in 2026 - Stats */}
        <div className="bg-purple-500/10 rounded-xl p-5 border border-purple-500/25 mb-5" data-testid="organic-reality-2026">
          <h3 className="text-purple-400 font-semibold text-sm uppercase tracking-wider mb-4">The Reality of Organic Reach in 2026</h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-slate-800/60 rounded-lg p-3 text-center border border-slate-700">
              <p className="text-3xl font-black text-blue-400">1.6%</p>
              <p className="text-slate-400 text-xs mt-1">Facebook Organic Reach</p>
              <p className="text-slate-500 text-[11px]">% of your followers who see your post</p>
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 text-center border border-slate-700">
              <p className="text-3xl font-black text-pink-400">3.5%</p>
              <p className="text-slate-400 text-xs mt-1">Instagram Organic Reach</p>
              <p className="text-slate-500 text-[11px]">% of your followers who see your post</p>
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 text-center border border-slate-700">
              <p className="text-3xl font-black text-blue-300">0.15%</p>
              <p className="text-slate-400 text-xs mt-1">Facebook Engagement Rate</p>
              <p className="text-slate-500 text-[11px]">Avg interactions per follower</p>
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 text-center border border-slate-700">
              <p className="text-3xl font-black text-pink-300">1.8%</p>
              <p className="text-slate-400 text-xs mt-1">Instagram Engagement Rate</p>
              <p className="text-slate-500 text-[11px]">Avg interactions per follower</p>
            </div>
          </div>
          <p className="text-slate-400 text-xs leading-relaxed">
            <span className="text-white font-medium">What this means:</span> If you have 1,000 followers on Facebook, only about 16 people see each post organically. On Instagram, about 35. These numbers have been declining year over year as algorithms prioritize paid content and personal connections over business pages.
          </p>
        </div>

        {/* Organic vs Paid Comparison */}
        <div className="mb-5" data-testid="organic-vs-paid">
          <h3 className="text-white font-semibold text-sm mb-3">Organic vs. Paid: Understanding the Difference</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/25">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/30 flex items-center justify-center">
                  <Heart className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <p className="text-emerald-400 font-semibold text-sm">Organic Social</p>
              </div>
              <ul className="space-y-2">
                {[
                  { label: 'Cost', value: 'Free (your time)' },
                  { label: 'Reach', value: '1.6–3.5% of followers' },
                  { label: 'Speed', value: 'Slow, compounds over months' },
                  { label: 'Trust', value: 'High: feels authentic' },
                  { label: 'Best for', value: 'Community, loyalty, brand voice' },
                  { label: 'Leads', value: '61% lower cost long-term' },
                ].map(item => (
                  <li key={item.label} className="flex justify-between text-xs">
                    <span className="text-slate-400">{item.label}</span>
                    <span className="text-slate-200 font-medium text-right">{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-violet-500/10 rounded-xl p-4 border border-violet-500/25">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-violet-500/30 flex items-center justify-center">
                  <TrendingUp className="w-3.5 h-3.5 text-violet-400" />
                </div>
                <p className="text-violet-400 font-semibold text-sm">Paid Social</p>
              </div>
              <ul className="space-y-2">
                {[
                  { label: 'Cost', value: 'Requires ad budget' },
                  { label: 'Reach', value: 'Scalable, targeted' },
                  { label: 'Speed', value: 'Immediate results' },
                  { label: 'Trust', value: 'Lower: seen as promotional' },
                  { label: 'Best for', value: 'Leads, sales, retargeting' },
                  { label: 'Stops when', value: 'You stop spending' },
                ].map(item => (
                  <li key={item.label} className="flex justify-between text-xs">
                    <span className="text-slate-400">{item.label}</span>
                    <span className="text-slate-200 font-medium text-right">{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Algorithm Changes */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-5 border border-slate-700" data-testid="algorithm-changes">
          <h3 className="text-amber-400 font-semibold text-sm mb-3">2026 Algorithm Changes: What You Need to Know</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Instagram className="w-3 h-3 text-pink-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">Instagram</p>
                <p className="text-slate-400 text-xs leading-relaxed">Now uses an interest-based algorithm over follower-based feeds. Reels get the biggest boost (4–8% engagement vs 1–3% for feed posts). Original, Instagram-native content is rewarded: reposts and aggregated content get penalized. Private shares (DMs) are now the strongest signal for distribution.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Facebook</p>
                <p className="text-slate-400 text-xs leading-relaxed">Organic reach continues to decline for business pages. The algorithm prioritizes content from friends and family over pages. However, Facebook Groups and community-driven content still perform well. Pair organic posts with Instagram for a hybrid approach.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Realistic Expectations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/25">
            <h3 className="text-purple-400 font-semibold text-sm mb-3">Realistic Expectations</h3>
            <ul className="space-y-2">
              {[
                'Results take 3–6 months of consistent posting',
                'Expect 1–3% of followers to see each post',
                'Video content (Reels) gets 2–4x the reach of static posts',
                'Posting 3–5x per week is the sweet spot for most businesses',
                'Engagement matters more than follower count',
                'A hybrid organic + paid strategy delivers 3.4x better ROI than either alone',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300 text-xs leading-relaxed">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/25">
            <h3 className="text-emerald-400 font-semibold text-sm mb-3">What Actually Works in 2026</h3>
            <ul className="space-y-2">
              {[
                'Original, short-form video (Reels/TikTok) dominates',
                'Behind-the-scenes content outperforms polished posts',
                'Carousels (up to 20 slides) drive high dwell time',
                'Customer stories and user-generated content build trust',
                'Responding to DMs and comments boosts algorithm ranking',
                'Repurpose your best organic content as paid ads',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300 text-xs leading-relaxed">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* The Hybrid Approach callout */}
        <div className="bg-gradient-to-r from-purple-500/15 to-pink-500/15 rounded-xl p-5 border border-purple-500/25 mb-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">3.4x</span>
            <p className="text-slate-300 text-sm">A hybrid organic + paid strategy delivers <span className="text-white font-semibold">3.4x better ROI</span> than using either approach alone</p>
          </div>
          <p className="text-slate-400 text-xs leading-relaxed">
            The most effective strategy: Use organic to validate content and build community, then amplify your best-performing posts with paid ads. Organic creates warm audiences: paid converts them.
          </p>
        </div>

        {/* Pro tip */}
        <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/25">
          <div className="flex items-start gap-2">
            <span className="text-emerald-400 font-bold text-sm mt-0.5">TIP</span>
            <p className="text-slate-300 text-sm leading-relaxed">
              Think of organic social as "staying in touch" with your community. Paid ads get their attention. Organic keeps the relationship alive. The businesses seeing the best results in 2026 are creating short-form video content (Reels), engaging authentically in comments and DMs, and using their organic insights to inform their paid strategy.
            </p>
          </div>
        </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const ChannelModal = ({ channel, onClose }) => {
  if (!channel) return null;
  const { modal } = channel;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed top-0 right-0 bottom-0 z-[101] w-[50%] max-w-[960px] bg-slate-900 shadow-2xl flex flex-col border-l border-slate-700"
      >
        {/* Header - prominent gradient bar */}
        <div className={`flex items-center justify-between px-5 py-4 flex-shrink-0 bg-gradient-to-r ${channel.gradient}`}>
          <div className="flex items-center gap-3 min-w-0 mr-4">
            <button onClick={onClose} className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0 font-medium" data-testid="close-channel-modal">
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back to Report</span>
            </button>
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">{modal.emoji}</span>
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">{modal.headline}</p>
              <p className="text-white/60 text-xs">{modal.tagline}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15 transition-colors">
            <X className="w-4 h-4 text-white/80" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Intro */}
          <div className="bg-slate-800/50 rounded-xl p-4 mb-5 border border-slate-700">
            <p className="text-slate-300 text-sm leading-relaxed">{modal.intro}</p>
          </div>

          {/* Stat callout */}
          <div className="flex items-center gap-4 rounded-xl p-4 mb-5 border" style={{ backgroundColor: `${channel.color}10`, borderColor: `${channel.color}30` }}>
            <span className="text-4xl font-black" style={{ color: channel.color }}>{modal.stat.value}</span>
            <p className="text-slate-300 text-sm">{modal.stat.label}</p>
          </div>

          {/* Content sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            {modal.sections.map((section, idx) => (
              <div key={idx} className="rounded-xl p-4 border" style={{ backgroundColor: `${section.color}08`, borderColor: `${section.color}25` }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: section.color }}>{section.title}</h3>
                <ul className="space-y-2">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: section.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Pro tip */}
          <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/25">
            <div className="flex items-start gap-2">
              <span className="text-emerald-400 font-bold text-sm mt-0.5">TIP</span>
              <p className="text-slate-300 text-sm leading-relaxed">{modal.tip}</p>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export const MarketingChannelsSection = ({ onOpenPPCModal, onOpenChannelGuide }) => {
  const [activeChannel, setActiveChannel] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <div className="relative z-10 mb-8" data-testid="marketing-channels-section">
        <div className="bg-gradient-to-r from-emerald-600/30 via-green-600/20 to-emerald-600/30 rounded-2xl p-8 border border-emerald-400/30">
          <button
            onClick={() => setIsOpen(v => !v)}
            data-testid="every-channel-toggle"
            className="w-full flex items-center gap-4 text-left group"
            aria-expanded={isOpen}
          >
            <div className="w-14 h-14 rounded-2xl bg-emerald-500/30 flex items-center justify-center flex-shrink-0 border border-emerald-400/30">
              <span className="text-2xl">🌱</span>
            </div>
            <div className="flex-1">
              <p className="text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-1">Your Marketing Ecosystem</p>
              <h3 className="text-2xl font-bold text-white">Every Channel Working Together</h3>
              <p className="text-emerald-300/80 text-xs mt-1 group-hover:text-emerald-200 transition-colors">
                {isOpen ? 'Click to collapse' : 'Click to learn more'}
              </p>
            </div>
            <ChevronDown
              className={`w-6 h-6 text-emerald-300 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                key="every-channel-content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pt-5">
                  <p className="text-slate-400 text-sm mb-5">
                    The most successful brands don't rely on a single channel: they create a <span className="text-emerald-400 font-semibold">connected experience</span> across every touchpoint. Learn how each channel plays a unique role in turning strangers into loyal customers.
                  </p>

                  {/* Channel cards grid — 4 across */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                    {channels.map((ch) => {
                      const Icon = ch.icon;
                      return (
                        <button
                          key={ch.id}
                          onClick={() => setActiveChannel(ch)}
                          className="group flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-xl p-3 border border-white/10 hover:border-white/20 transition-all text-left cursor-pointer"
                          data-testid={`channel-card-${ch.id}`}
                        >
                          <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${ch.gradient} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm">{ch.title}</p>
                            <p className="text-slate-500 text-[11px] truncate">{ch.oneLiner}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors flex-shrink-0" />
                        </button>
                      );
                    })}

                    {/* PPC Guide — same card style */}
                    <button 
                      onClick={onOpenPPCModal}
                      className="group flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-xl p-3 border border-white/10 hover:border-white/20 transition-all text-left cursor-pointer"
                      data-testid="need-ppc-cta"
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm">PPC & Search Marketing</p>
                        <p className="text-slate-500 text-[11px] truncate">Understanding paid search ads</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors flex-shrink-0" />
                    </button>

                    {/* Marketing Channel Guide — same card style */}
                    <button 
                      onClick={onOpenChannelGuide}
                      className="group flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-xl p-3 border border-white/10 hover:border-white/20 transition-all text-left cursor-pointer"
                      data-testid="marketing-channel-guide-cta"
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm">Marketing Channel Guide</p>
                        <p className="text-slate-500 text-[11px] truncate">Your advertising channels explained</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 transition-colors flex-shrink-0" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Channel detail modal */}
      {activeChannel?.isRich ? (
        <OrganicSocialModal channel={activeChannel} onClose={() => setActiveChannel(null)} />
      ) : (
        <ChannelModal channel={activeChannel} onClose={() => setActiveChannel(null)} />
      )}
    </>
  );
};
